<template>
  <v-list-tile
    avatar
    class="list-tile-min-height list-tile-auto-height selectable"
  >
    <!-- Decoder Icon -->
    <v-list-tile-avatar>
      <v-icon
        v-if="decoder.ctype === 'radio'"
        :class="{
          'msr--text': !decoder.is_silenced,
          'grey--text': decoder.is_silenced,
        }"
        large
      >
        radio
      </v-icon>
      <icon-tv-decoder
        v-if="decoder.ctype === 'tv'"
        :tvdecoder="decoder"
        color="msr"
        large
      />
      <v-icon v-if="decoder.ctype === 'touch'">
        touch_app
      </v-icon>
    </v-list-tile-avatar>
    <v-layout
      :align-center="$vuetify.breakpoint.mdAndUp"
      :justify-center="$vuetify.breakpoint.smAndDown"
      v-bind="layout"
    >
      <v-list-tile-content style="min-width: 200px">
        <v-list-tile-title>
          <!-- Description -->
          <strong
            class="grey--text text--darken-3"
            data-cy="decoderdetail-description-title"
          >{{ decoder.description }}</strong>
          <!-- Rename Decoder Button -->
          <v-btn
            v-if="permissions.rename"
            icon
            small
            class="rename-btn ml-1 mr-2"
            :title="$t('renameDecoder')"
            data-cy="rename-decoder-btn"
            @click="$emit('rename')"
          >
            <v-icon
              color="msr"
              small
            >
              edit
            </v-icon>
          </v-btn>
        </v-list-tile-title>
        <v-list-tile-sub-title
          :class="{clickable: hasSlaves && playerSubTitleTooltip}"
          :style="{'white-space': expandSubTitle ? 'normal' : 'nowrap'}"
          @click="() => (hasSlaves ? (expandSubTitle = !expandSubTitle) : null)"
        >
          <span :title="playerSubTitleTooltip">{{ playerSubTitle }}</span>
        </v-list-tile-sub-title>
        <!-- Missing Store Opening Hours Warning -->
        <div
          v-if="decoder.ctype === 'radio' && !decoder.store.has_opening_hours"
          class="caption"
        >
          {{ $t('openinghoursMissingForThisStore') }}:
          <router-link to="/openinghours">
            {{ $t('closingtimeManageOpeningHoursNow') }}
          </router-link>
        </div>
      </v-list-tile-content>
      <!-- Current Tags -->
      <div v-if="decoder.id && $userprofile && $userprofile.can_use_tagging">
        <tag-select-list
          :tags="decoder.tags"
          :removable="permissions.tag"
          removable-tooltip="removeTagFromDecoder"
          :xs="$vuetify.breakpoint.xs"
          @remove="removeTag"
        />
      </div>
    </v-layout>
    <!-- 'Read only' -->
    <v-list-tile-avatar
      v-if="decoder.id && decoder.read_only"
      class="clickable"
    >
      <v-icon
        medium
        class="warning--text"
        @click="alertReadOnly()"
      >
        lock
      </v-icon>
    </v-list-tile-avatar>
    <!-- Assign/remove Tags -->
    <tag-select
      v-if="
        decoder.ctype != 'touch' &&
          !decoder.ignore_tagging &&
          $userprofile &&
          $userprofile.can_use_tagging &&
          permissions.tag
      "
      :tags="compatibleUserTagsNotYetAddedToDecoder"
      add-tooltip="addTagToDecoder"
      medium
      @add="addTag"
    />
    <!-- Batch Actions Menu -->
    <v-menu
      v-if="
        !decoder.read_only &&
          !decoder.is_silenced &&
          hasEntries &&
          !onlyRadioTagEntries
      "
      offset-y
      left
    >
      <v-btn
        slot="activator"
        color="grey--text text--darken-1"
        icon
        class="ma-0"
        data-cy="batch-actions-btn"
      >
        <v-icon
          class="hoverable-icon"
          medium
        >
          more_vert
        </v-icon>
      </v-btn>
      <v-list>
        <v-list-tile>
          <v-list-tile-title>
            <strong class="msr--text">{{
              $t('forMultiple') + ' ' + $t('contents')
            }}</strong>
          </v-list-tile-title>
        </v-list-tile>
        <v-divider />
        <!-- Timing -->
        <v-list-tile
          v-if="noEntriesWithExpiredSpot"
          @click="$emit('toggle-timing')"
        >
          <v-list-tile-avatar>
            <v-icon
              :class="{
                'grey--text': !allEntriesUseTiming(entries),
                'msr--text': allEntriesUseTiming(entries),
              }"
            >
              timer
            </v-icon>
          </v-list-tile-avatar>
          {{ $t('timing') }}
        </v-list-tile>
        <!-- Deactivate -->
        <v-list-tile
          data-cy="batch-deactivate-all-entries-tile"
          @click="$emit('toggle-removing')"
        >
          <v-list-tile-avatar>
            <v-icon class="grey--text">
              block
            </v-icon>
          </v-list-tile-avatar>
          {{ $t('deactivate') }}
        </v-list-tile>
        <!-- Reactivate TagEntries -->
        <v-list-tile
          v-if="hasDeactivatedTagEntries"
          @click="$emit('toggle-reactivating')"
        >
          <v-list-tile-avatar>
            <v-icon class="grey--text">
              play_arrow
            </v-icon>
          </v-list-tile-avatar>
          {{ $t('reactivate') }}
        </v-list-tile>
        <!-- Filter by timeframe -->
        <timeframe-filter-list-tile :entries="entries" />
      </v-list>
    </v-menu>
  </v-list-tile>
</template>

<script>
import {addTagToDecoder, removeTagFromDecoder} from '@/mixins/tags';
import {alertReadOnly, allEntriesUseTiming} from '@/mixins/commonEntry.js';
import identifiers from '@/mixins/identifiers.js';

import IconTvDecoder from '@/components/IconTvDecoder';
import TagSelectList from '@/components/TagSelectList';
import TagSelect from '@/components/TagSelect';
import TimeframeFilterListTile from '@/components/TimeframeFilterListTile';

export default {
  components: {
    IconTvDecoder,
    TagSelectList,
    TagSelect,
    TimeframeFilterListTile,
  },
  mixins: [identifiers],
  props: {
    decoder: {
      type: Object,
      required: true,
    },
    entries: {
      type: Array[Object],
      required: true,
    },
  },
  data() {
    return {
      expandSubTitle: false,
    };
  },
  computed: {
    hasEntries() {
      return this.entries.length > 0;
    },
    onlyRadioTagEntries() {
      const numEntries = this.entries.length;
      return (
        numEntries > 0 &&
        this.entries.filter((entry) => this.isRadioTagEntry(entry)).length ==
          numEntries
      );
    },
    hasDeactivatedTagEntries() {
      return (
        this.entries
          .filter((entry) => this.isTagEntry(entry))
          .filter((entry) => !entry.active).length > 0
      );
    },
    layout() {
      if (this.$vuetify.breakpoint.smAndDown) {
        return {column: true};
      }
      return {row: true};
    },
    compatibleUserTagsNotYetAddedToDecoder() {
      if (!this.$userprofile) {
        return [];
      }

      return this.$store.state.common.tags.filter((tag) => {
        // Tags and the thing we want to use them on must be in the same Group.
        // Tags and the thing we want to use them on must be in the same
        // Group. Since decoder.store.groups contains parent Group IDs,
        // this e.g. ensures we can inherit Tag access properly like
        // with content.
        const matchingGroup =
          this.decoder.store.groups.indexOf(tag.group) !== -1;
        if (!matchingGroup) {
          return false;
        }

        // Ignore Tag if already assigned.
        for (const decoderTag of this.decoder.tags) {
          if (tag.id === decoderTag.id) {
            return false;
          }
        }

        return true;
      });
    },
    permissions() {
      if (!this.decoder.id || !this.$user) {
        return {};
      }
      const userHasWriteAccessToDecoder = !this.decoder.read_only;
      return {
        rename: userHasWriteAccessToDecoder,
        tag:
          this.$store.getters.userManagesContentForAtLeastOneGroup &&
          this.$userprofile.can_use_tagging &&
          userHasWriteAccessToDecoder,
      };
    },
    hasSlaves() {
      return (
        this.decoder.slave_decoder_serials_and_descriptions &&
        this.decoder.slave_decoder_serials_and_descriptions.length > 0
      );
    },
    playerSubTitleTooltip() {
      if (
        !this.hasSlaves ||
        this.decoder.slave_decoder_serials_and_descriptions.length <= 2
      ) {
        return null;
      }
      return this.expandSubTitle
        ? this.$t('clickToCollapse')
        : this.$t('clickToExpand');
    },
    playerSubTitle() {
      // Translation IDs used here are: radioPlayer, tvPlayer,
      //   radioPlayerMaster, tvPlayerMaster.

      let key = this.decoder.ctype + 'Player';
      if (this.hasSlaves) {
        key = this.decoder.ctype + 'PlayerMaster';
      }
      let translation = this.$t(key);
      translation = translation.replace('{{ serial }}', this.decoder.serial);
      if (this.hasSlaves) {
        translation = translation.replace(
          '{{ numSlaves }}',
          this.decoder.slave_decoder_serials_and_descriptions.length
        );
        translation +=
          ' ' +
          this.decoder.slave_decoder_serials_and_descriptions
            .map((obj) => obj['description'])
            .join(', ');
      }
      return translation;
    },
    noEntriesWithExpiredSpot() {
      const entriesWithExpiredSpot = this.entries.filter(
        (entry) => entry.spot && entry.spot.expired
      );
      return entriesWithExpiredSpot.length === 0;
    },
  },
  methods: {
    alertReadOnly: alertReadOnly,
    allEntriesUseTiming: allEntriesUseTiming,
    addTag(tag) {
      return addTagToDecoder(tag, this.decoder)
        .then(() => {
          this.$emit('tags-changed');
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        });
    },
    removeTag(tag) {
      return removeTagFromDecoder(tag, this.decoder)
        .then(() => {
          this.$emit('tags-changed');
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        });
    },
  },
};
</script>

<style scoped>
.rename-btn {
  width: 24px;
  height: 24px;
  margin: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/DecoderDetail/DecoderDetailHeader.vue